import { useState, useEffect, useCallback } from 'react';
import useVrackApi from './useVrackApi';
import { formatMonthParam } from '../components/billing/MonthPicker';

/**
 * Hook to fetch a single company's billing report from
 * /billing/companies/{companyId}/billing-report
 * @param {Object} opts
 * @param {string} opts.companyId - Company ID from the route
 * @param {Date|string|null} opts.month - Billing month as a Date instance (from the MUI
 *   DatePicker) or an already-formatted "YYYY-MM" string (e.g., "2026-08"). Normalized
 *   to YYYY-MM before being sent to the API.
 * @returns {{
 *   company: Object|null,
 *   loading: boolean,
 *   error: Error|null,
 *   invoiceNotFound: boolean,
 *   refetch: Function
 * }}
 */
export default function useCompanyBillingReport({ companyId, month = null } = {}) {
  const [company, setCompany] = useState(null);
  const [invoiceNotFound, setInvoiceNotFound] = useState(false);

  const { loading, error, fetchData } = useVrackApi(
    'get',
    `/billing/companies/${companyId}/billing-report`
  );

  const fetchReport = useCallback(
    async (signal) => {
      if (!companyId) return;
      // Reset transient state at the start of every fetch so a successful
      // refetch (e.g. after Refresh Invoice generates the invoice) clears
      // the not-found flag, and so navigating to a different company does
      // not leak the previous one's chrome while the new fetch is in flight.
      if (!signal?.aborted) {
        setInvoiceNotFound(false);
        setCompany(null);
      }
      try {
        const params = {};
        const monthParam = formatMonthParam(month);
        if (monthParam) {
          params.month = monthParam;
        }
        const response = await fetchData({ params, signal });

        if (response) {
          const billableTeams = (response.billable_teams || []).map((team) => {
            const billableParts = team.billable_parts || [];
            const billableResources = billableParts.reduce(
              (sum, part) => sum + (part.billable_quantity || 0),
              0
            );
            return {
              id: team.team_name,
              name: team.team_name,
              managerEmail: team.manager,
              costContribution: team.cost_contribution_percent,
              usageCost: team.team_usage_cost,
              billableResources,
              billableParts
            };
          });

          const billableResources = billableTeams.reduce(
            (sum, team) => sum + (team.billableResources || 0),
            0
          );

          // Aggregate `billable_parts` across every team for this company,
          // grouping by part_number. The company report shows a high-level
          // view per part number — total billable quantity and total cost
          // — without breaking down per team. `unit_price` is taken from
          // the first occurrence; the API returns a single price per part
          // number, so they're consistent across teams.
          const partNumberMap = new Map();
          billableTeams.forEach((team) => {
            (team.billableParts || []).forEach((part) => {
              const existing = partNumberMap.get(part.part_number) || {
                id: part.part_number,
                profile: '',
                description: '',
                billableQuantity: 0,
                unitPrice: part.unit_price ?? 0,
                monthlyCharge: 0
              };
              existing.billableQuantity += part.billable_quantity ?? 0;
              existing.monthlyCharge += part.part_total_cost ?? 0;
              partNumberMap.set(part.part_number, existing);
            });
          });
          const partNumbers = Array.from(partNumberMap.values()).sort((a, b) =>
            a.id.localeCompare(b.id)
          );

          const mapped = {
            id: response.company_id,
            name: response.company_name || '-',
            invoiceNumber: response.invoice_number,
            monthlyCharges: response.invoice_cost ?? 0,
            platformFee: response.platform_fee ?? 0,
            teamUsageCost: response.lru_usage_cost ?? 0,
            teamsCount: response.team_count ?? 0,
            billingMonth: response.billing_month,
            generatedAt: response.generated_at,
            billableResources,
            partNumbers,
            teams: billableTeams
          };

          if (!signal?.aborted) {
            setCompany(mapped);
          }
        }
      } catch (err) {
        if (signal?.aborted || err?.code === 'ERR_CANCELED') return;
        console.error('Failed to fetch company billing report:', err);
        // Distinguish "no invoice for this company/month yet" (404) from
        // genuine fetch failures (network/5xx/etc). The page renders a
        // recovery affordance for the former and a generic retry for the
        // latter — both are surfaced as distinct state so the page can
        // branch on them.
        if (err?.response?.status === 404) {
          // No invoice for this company/month yet. `company` was already
          // reset to null at the top of fetchReport, so the page renders
          // the invoice-not-found banner via the `invoiceNotFound` flag
          // rather than showing stale chrome for a now-missing invoice.
          setInvoiceNotFound(true);
        } else {
          setInvoiceNotFound(false);
          setCompany(null);
        }
      }
    },
    [companyId, month, fetchData]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchReport(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, month]);

  return {
    company,
    loading,
    error,
    invoiceNotFound,
    refetch: fetchReport
  };
}
